import React, { useEffect } from "react";
import GlobalDialog from "../../../../../components/mui/Dialogue/GlobalDialog";
import {
  Box,
  Button,
  Chip,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import Notify from "./Notify";
import CompleteRestartCycle from "./CompleteRestartCycle";
import Publish from "./Publish";
import { useDispatch, useSelector } from "react-redux";
import { getCycleStatus } from "../../../../../redux/features/appraisalSlice";

const CircularProgressWithTotal = ({ total, completed, pending }) => {
  const percentage = total > 0 ? (completed / total) * 100 : 0;

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Box position="relative" display="inline-flex">
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={100}
          thickness={2}
        />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <Typography variant="h6" component="div">
            {`${Math.round(percentage)}%`}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Total: {total}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Completed: {completed}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Pending: {pending}
        </Typography>
      </Box>
    </Box>
  );
};

const CycleStatus = ({ open, setOpen, currentData, id, status }) => {
  const dispatch = useDispatch();
  const { cycleStatus, loading } = useSelector((state) => state.appraisal);

  useEffect(() => {
    if (open) {
      dispatch(getCycleStatus(id));
    }
  }, [open]);

  const Card = ({ icon, title, period, total, completed, pending }) => {
    return (
      <Box mt={1}>
        <Paper
          elevation={2}
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            p: 1,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", p: 1 }}>
            <Box
              sx={{
                backgroundColor: "#2f72b342",
                color: "#2f72b3",
                height: 40,
                width: 40,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 1,
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="subtitle1">{title}</Typography>
              <Typography variant="body2">{period}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {title === "Publish Status" ? (
                <>{status === 2 ? null : <Publish id={id} />}</>
              ) : (
                <>{status === 2 ? null : <Notify title={title} id={id} />}</>
              )}
            </Box>

            <CircularProgressWithTotal
              pending={pending}
              total={total}
              completed={completed}
            />
          </Box>
        </Paper>
      </Box>
    );
  };

  return (
    <GlobalDialog
      open={open}
      setOpen={setOpen}
      title={currentData?.appraisal_cycle_name || "Cycle Status"}
      fullScreen
      closeButton={true}
      content={
        <>
          {open ? (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Chip
                  size="small"
                  label={`Appraisal Process Period: ${
                    cycleStatus?.appraisalProcessPeriod || "N/A"
                  }`}
                />
                <Chip
                  size="small"
                  label={`Appraisal Evaluation Period: ${
                    cycleStatus?.appraisalEvaluationPeriod || "N/A"
                  }`}
                />
              </Box>

              <Paper
                elevation={0}
                sx={{ mt: 2, p: 2, backgroundColor: "grey.200" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Chip
                    variant="outlined"
                    size="small"
                    color="primary"
                    label={`Total Appraisees: ${
                      cycleStatus?.totalAppraisees ?? 0
                    }`}
                  />
                  <Box>
                    <Typography>{`Completed: ${
                      cycleStatus?.completed ?? 0
                    }`}</Typography>
                  </Box>
                  <Box>
                    <Typography>{`Pending: ${
                      cycleStatus?.pending ?? 0
                    }`}</Typography>
                  </Box>
                  <Box>
                    {status === 2 ? null : <CompleteRestartCycle id={id} />}
                  </Box>
                </Box>
              </Paper>

              {/* Self Appraisal Card */}
              {cycleStatus?.statuses?.selfAppraisal && (
                <Card
                  title={cycleStatus.statuses.selfAppraisal.title}
                  period={cycleStatus.statuses.selfAppraisal.period || "N/A"}
                  icon={
                    <Icon icon="iconoir:user-star" width="24" height="24" />
                  }
                  total={
                    (cycleStatus.statuses.selfAppraisal.completed || 0) +
                    (cycleStatus.statuses.selfAppraisal.pending || 0)
                  }
                  completed={cycleStatus.statuses.selfAppraisal.completed || 0}
                  pending={cycleStatus.statuses.selfAppraisal.pending || 0}
                />
              )}

              {/* Manager Review Card */}
              {cycleStatus?.statuses?.managerReview && (
                <Card
                  title={cycleStatus.statuses.managerReview.title}
                  period={cycleStatus.statuses.managerReview.period || "N/A"}
                  icon={
                    <Icon icon="iconoir:user-star" width="24" height="24" />
                  }
                  total={
                    (cycleStatus.statuses.managerReview.completed || 0) +
                    (cycleStatus.statuses.managerReview.pending || 0)
                  }
                  completed={cycleStatus.statuses.managerReview.completed || 0}
                  pending={cycleStatus.statuses.managerReview.pending || 0}
                />
              )}

              {/* Publish Status Card */}
              {cycleStatus?.statuses?.publish && (
                <Card
                  title={cycleStatus.statuses.publish.title}
                  icon={
                    <Icon icon="iconoir:user-star" width="24" height="24" />
                  }
                  total={
                    (cycleStatus.statuses.publish.published || 0) +
                    (cycleStatus.statuses.publish.unpublished || 0)
                  }
                  completed={cycleStatus.statuses.publish.published || 0}
                  pending={cycleStatus.statuses.publish.unpublished || 0}
                />
              )}
            </Box>
          ) : null}
        </>
      }
    />
  );
};

export default CycleStatus;
